import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import { useNavigate } from '@tanstack/react-router';

import { useActionBar } from '@/lib/context/ActionBarContext';
import { useAuth } from '@/lib/hooks/useAuth';
import { useBackNavigation } from '@/lib/hooks/useBackNavigation';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import {
  useEvent,
  useUpdateEvent,
  useCreateEvent,
  useDeleteEvent,
  useCreateEventSeries,
} from '@/lib/hooks/useEvents';
import { eventFormSchema } from '@/lib/validators/event-form.validator';
import {
  eventFormDefaultValues,
  extractDateOnly,
  formatTimeForInput,
} from '@/lib/utils/events.utils';
import { sendEventNotification } from '@/lib/services/notifications';
import {
  listEventGroupTargets,
  listEventRoleTargets,
  resolveEventAudiencePreview,
  setEventGroupTargets,
  setEventRoleTargets,
} from '@/lib/services/event-targets';
import type { EventFormValues, EventFormProps } from '@/lib/types/events.types';
import type { EventUpdate, EventInsert, UserRole } from '@/lib/types/database';
import {
  EVENT_FILE_MAX_COUNT,
  type EventFileFormItem,
} from '@/lib/types/event-files.types';
import {
  buildEventDescriptionPayload,
  getEventDescriptionEditorValue,
} from '@/lib/utils/event-description.utils';
import {
  createEventFileClientId,
  eventFileToFormItem,
  getDefaultOccurrenceIndexes,
  getVisibleEventFileCount,
  sanitizeEventFileTitle,
  validateEventFile,
  validateEventFileItems,
} from '@/lib/utils/event-files.utils';
import { useEventFiles, useSyncEventFiles } from '@/lib/hooks/useEventFiles';
import { getFilesForOccurrence } from '@/lib/services/event-files';

/**
 * Type for the event form instance returned by useEventForm
 */
export type EventFormInstance = ReturnType<typeof useEventForm>['form'];

/**
 * Convert Event to EventFormValues
 */
function eventToFormValues(
  event: NonNullable<ReturnType<typeof useEvent>['data']>,
  roleTargets?: UserRole[],
  groupTargetIds?: string[]
): EventFormValues {
  const invitationGroups = new Set<UserRole>(
    roleTargets && roleTargets.length > 0
      ? roleTargets
      : ((event.invitation_groups || []) as UserRole[])
  );

  return {
    title: event.title || '',
    description: getEventDescriptionEditorValue(event),
    location: event.location || '',
    eventDate: event.start_time ? extractDateOnly(event.start_time) : '',
    startTime: event.start_time ? formatTimeForInput(event.start_time) : '',
    endTime: event.end_time ? formatTimeForInput(event.end_time) : undefined,
    eventType: event.event_type || '',
    rsvpDeadline: event.rsvp_deadline
      ? extractDateOnly(event.rsvp_deadline)
      : undefined,
    capacity: event.capacity || undefined,
    invitationGroups,
    eventGroupTargetIds: new Set(groupTargetIds ?? []),
    eventLink: event.event_link || '',
    linkName: event.link_name || '',
    recurrenceType: 'none', // For MVP, treat series events as individual events in edit mode
    recurrenceCount: 2,
  };
}

/**
 * Convert EventFormValues to EventUpdate format
 */
function formValuesToEventUpdate(values: EventFormValues): EventUpdate {
  // Combine eventDate and startTime into ISO string for start_time
  const startTime =
    values.eventDate && values.startTime
      ? new Date(`${values.eventDate}T${values.startTime}`).toISOString()
      : undefined;

  // Combine eventDate and endTime into ISO string for end_time
  const endTime =
    values.eventDate && values.endTime
      ? new Date(`${values.eventDate}T${values.endTime}`).toISOString()
      : undefined;

  // Convert rsvpDeadline to ISO string if provided
  const rsvpDeadline = values.rsvpDeadline
    ? new Date(`${values.rsvpDeadline}T00:00:00`).toISOString()
    : undefined;

  // Convert invitationGroups Set to array for database
  const invitationGroups = values.invitationGroups
    ? Array.from(values.invitationGroups)
    : [];

  return {
    title: values.title || undefined,
    ...buildEventDescriptionPayload(values.description),
    location: values.location || undefined,
    start_time: startTime,
    end_time: endTime,
    rsvp_deadline: rsvpDeadline || null,
    capacity: values.capacity || null,
    invitation_groups: invitationGroups,
    event_link:
      values.eventLink && values.eventLink.trim() !== ''
        ? values.eventLink.trim()
        : null,
    link_name:
      values.linkName && values.linkName.trim() !== ''
        ? values.linkName.trim()
        : null,
    event_type:
      values.eventType && values.eventType.trim() !== ''
        ? values.eventType.trim()
        : null,
  };
}

/**
 * Convert EventFormValues to EventInsert format
 */
function formValuesToEventInsert(
  values: EventFormValues,
  createdBy: string
): EventInsert {
  // Combine eventDate and startTime into ISO string for start_time
  const startTime =
    values.eventDate && values.startTime
      ? new Date(`${values.eventDate}T${values.startTime}`).toISOString()
      : '';

  // Combine eventDate and endTime into ISO string for end_time
  // If endTime is not provided, default to 1 hour after startTime
  let endTime: string;
  if (values.eventDate && values.endTime) {
    endTime = new Date(`${values.eventDate}T${values.endTime}`).toISOString();
  } else if (values.eventDate && values.startTime) {
    // Default to 1 hour after start time
    const start = new Date(`${values.eventDate}T${values.startTime}`);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // Add 1 hour
    endTime = end.toISOString();
  } else {
    endTime = '';
  }

  // Convert rsvpDeadline to ISO string if provided
  const rsvpDeadline = values.rsvpDeadline
    ? new Date(`${values.rsvpDeadline}T00:00:00`).toISOString()
    : null;

  // Convert invitationGroups Set to array for database
  const invitationGroups = values.invitationGroups
    ? Array.from(values.invitationGroups)
    : [];

  return {
    title: values.title || '',
    ...buildEventDescriptionPayload(values.description),
    location: values.location || null,
    start_time: startTime,
    end_time: endTime,
    rsvp_deadline: rsvpDeadline,
    capacity: values.capacity || null,
    is_cancelled: false,
    invitation_groups: invitationGroups.length > 0 ? invitationGroups : null,
    event_link:
      values.eventLink && values.eventLink.trim() !== ''
        ? values.eventLink.trim()
        : null,
    link_name:
      values.linkName && values.linkName.trim() !== ''
        ? values.linkName.trim()
        : null,
    event_type:
      values.eventType && values.eventType.trim() !== ''
        ? values.eventType.trim()
        : null,
    created_by: createdBy,
  };
}

/**
 * Custom hook for managing event form state and actions
 */
export function useEventForm({ mode, eventId }: EventFormProps) {
  const isEditMode = mode === 'edit';
  const { user } = useAuth();
  const handleBack = useBackNavigation();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToastNotification();
  const updateEvent = useUpdateEvent();
  const createEvent = useCreateEvent();
  const deleteEvent = useDeleteEvent();
  const createEventSeries = useCreateEventSeries();
  const syncEventFiles = useSyncEventFiles();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sendNotification, setSendNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [deleteDrawerOpen, setDeleteDrawerOpen] = useState(false);
  const [materialFiles, setMaterialFiles] = useState<EventFileFormItem[]>([]);
  const [materialErrors, setMaterialErrors] = useState<string[]>([]);
  const materialFilesRef = useRef<EventFileFormItem[]>([]);
  materialFilesRef.current = materialFiles;

  // Load event data when in edit mode
  const { data: event, isLoading: isLoadingEvent } = useEvent(
    isEditMode && eventId ? eventId : 0
  );
  const { data: existingEventFiles, isLoading: isLoadingEventFiles } =
    useEventFiles(isEditMode && eventId ? eventId : 0);
  const [eventRoleTargets, setEventRoleTargetsState] = useState<
    UserRole[] | null
  >(null);
  const [eventGroupTargetIds, setEventGroupTargetIdsState] = useState<
    string[] | null
  >(null);
  const [isLoadingEventRoleTargets, setIsLoadingEventRoleTargets] =
    useState(false);
  const [isLoadingEventGroupTargets, setIsLoadingEventGroupTargets] =
    useState(false);

  useEffect(() => {
    if (!isEditMode || !eventId) {
      setEventRoleTargetsState(null);
      setEventGroupTargetIdsState(null);
      return;
    }

    let isCurrent = true;
    setIsLoadingEventRoleTargets(true);

    listEventRoleTargets(eventId)
      .then((roles) => {
        if (isCurrent) {
          setEventRoleTargetsState(roles);
        }
      })
      .catch((error) => {
        console.error('Failed to load event role targets:', error);
        if (isCurrent) {
          setEventRoleTargetsState([]);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingEventRoleTargets(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [eventId, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !eventId) {
      setEventGroupTargetIdsState(null);
      return;
    }

    let isCurrent = true;
    setIsLoadingEventGroupTargets(true);

    listEventGroupTargets(eventId)
      .then((groups) => {
        if (isCurrent) {
          setEventGroupTargetIdsState(groups.map((group) => group.id));
        }
      })
      .catch((error) => {
        console.error('Failed to load event group targets:', error);
        if (isCurrent) {
          setEventGroupTargetIdsState([]);
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoadingEventGroupTargets(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [eventId, isEditMode]);

  // Compute initial form values - use event data if available, otherwise use defaults
  const initialFormValues = useMemo(() => {
    if (
      isEditMode &&
      event &&
      !isLoadingEvent &&
      !isLoadingEventRoleTargets &&
      !isLoadingEventGroupTargets
    ) {
      return eventToFormValues(
        event,
        eventRoleTargets ?? undefined,
        eventGroupTargetIds ?? undefined
      );
    }
    return eventFormDefaultValues;
  }, [
    isEditMode,
    event,
    eventRoleTargets,
    eventGroupTargetIds,
    isLoadingEvent,
    isLoadingEventRoleTargets,
    isLoadingEventGroupTargets,
  ]);

  const formId = useMemo(() => {
    if (!isEditMode) {
      return 'event-form-create';
    }

    return `event-form-edit-${eventId ?? 'unknown'}-${
      isLoadingEvent || isLoadingEventRoleTargets || isLoadingEventGroupTargets
        ? 'loading'
        : 'ready'
    }`;
  }, [
    eventId,
    isEditMode,
    isLoadingEvent,
    isLoadingEventRoleTargets,
    isLoadingEventGroupTargets,
  ]);

  const form = useForm({
    formId,
    defaultValues: initialFormValues,
    onSubmit: async ({ value }: { value: EventFormValues }) => {
      console.log('TODO: Implement submitting event payload', value);
    },
  });
  const formRef = useRef(form);
  formRef.current = form;
  const recurrenceState = useStore(form.store, (state) => ({
    recurrenceType: state.values.recurrenceType,
    recurrenceCount: state.values.recurrenceCount,
  }));

  // Track whether form submission has been attempted (to show all errors)
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Store validation errors from Zod
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    if (!isEditMode || isLoadingEventFiles || !existingEventFiles) {
      return;
    }

    setMaterialFiles(existingEventFiles.map(eventFileToFormItem));
  }, [existingEventFiles, isEditMode, isLoadingEventFiles]);

  const isRecurringCreate =
    !isEditMode &&
    recurrenceState.recurrenceType !== 'none' &&
    recurrenceState.recurrenceCount >= 2;

  const recurrenceCount = isRecurringCreate
    ? recurrenceState.recurrenceCount
    : 1;

  const addMaterialFiles = (files: FileList | File[]) => {
    const incomingFiles = Array.from(files);
    const remainingSlots = Math.max(
      0,
      EVENT_FILE_MAX_COUNT - getVisibleEventFileCount(materialFiles)
    );

    const nextFiles = incomingFiles.slice(0, remainingSlots).map((file) => ({
      id: createEventFileClientId(),
      kind: 'draft' as const,
      displayTitle: sanitizeEventFileTitle(file.name),
      originalFilename: file.name,
      sizeBytes: file.size,
      file,
      targetOccurrenceIndexes: getDefaultOccurrenceIndexes(recurrenceCount),
      error: validateEventFile(file),
    }));

    setMaterialFiles((current) => [...current, ...nextFiles]);

    if (incomingFiles.length > remainingSlots) {
      setMaterialErrors([
        `Events can have up to ${EVENT_FILE_MAX_COUNT} PDFs.`,
      ]);
    } else {
      setMaterialErrors([]);
    }
  };

  const updateMaterialTitle = (id: string, value: string) => {
    setMaterialFiles((current) =>
      current.map((file) =>
        file.id === id ? { ...file, displayTitle: value } : file
      )
    );
  };

  const removeMaterialFile = (id: string) => {
    setMaterialFiles((current) =>
      current
        .map((file) =>
          file.id === id && file.kind === 'existing'
            ? { ...file, markedForDeletion: true }
            : file
        )
        .filter((file) => file.kind === 'existing' || file.id !== id)
    );
  };

  const restoreMaterialFile = (id: string) => {
    setMaterialFiles((current) =>
      current.map((file) =>
        file.id === id ? { ...file, markedForDeletion: false } : file
      )
    );
  };

  const toggleMaterialOccurrence = (id: string, occurrenceIndex: number) => {
    setMaterialFiles((current) =>
      current.map((file) => {
        if (file.id !== id) {
          return file;
        }

        const targets = new Set(file.targetOccurrenceIndexes);
        if (targets.has(occurrenceIndex)) {
          targets.delete(occurrenceIndex);
        } else {
          targets.add(occurrenceIndex);
        }

        return {
          ...file,
          targetOccurrenceIndexes: Array.from(targets).sort((a, b) => a - b),
        };
      })
    );
  };

  // Handle opening the drawer - validate form first
  const handleOpenDrawer = async () => {
    // Mark that submission was attempted - this will trigger error display
    setSubmitAttempted(true);

    // Validate form before opening drawer
    const formValues = formRef.current.state.values;
    const validationResult = eventFormSchema.safeParse(formValues);

    if (!validationResult.success) {
      const flattened = validationResult.error.flatten();
      // Store the field errors so components can display them
      setValidationErrors(flattened.fieldErrors as Record<string, string[]>);
      return;
    }

    const fileErrors = validateEventFileItems(materialFilesRef.current);

    if (fileErrors.length > 0) {
      setMaterialErrors(fileErrors);
      return;
    }

    const selectedRoles = formValues.invitationGroups
      ? Array.from(formValues.invitationGroups)
      : [];
    const selectedGroupIds = formValues.eventGroupTargetIds
      ? Array.from(formValues.eventGroupTargetIds)
      : [];

    try {
      const previewMembers = await resolveEventAudiencePreview({
        roleTargets: selectedRoles,
        groupTargetIds: selectedGroupIds,
      });

      if (previewMembers.length === 0) {
        setValidationErrors({
          invitationGroups: [
            'Selected audience targets currently resolve to zero members.',
          ],
        });
        return;
      }
    } catch (error) {
      setValidationErrors({
        invitationGroups: [
          error instanceof Error
            ? error.message
            : 'Audience preview could not be calculated.',
        ],
      });
      return;
    }

    // Validation passed - clear errors and open drawer
    setValidationErrors({});
    setMaterialErrors([]);
    setDrawerOpen(true);
  };

  const isFormLoading =
    isEditMode &&
    (isLoadingEvent ||
      isLoadingEventFiles ||
      isLoadingEventRoleTargets ||
      isLoadingEventGroupTargets);

  useActionBar({
    title: 'Review & Publish',
    visible: !isFormLoading,
    actions: [
      {
        key: 'cancel',
        label: 'Cancel',
        variant: 'secondary',
        fullWidth: true,
        onClick: handleBack,
      },
      {
        key: 'primary',
        label: isEditMode ? 'Update event' : 'Publish',
        variant: 'default',
        fullWidth: true,
        loading: isEditMode
          ? updateEvent.isPending || syncEventFiles.isPending
          : createEvent.isPending ||
            createEventSeries.isPending ||
            syncEventFiles.isPending,
        onClick: () => {
          void handleOpenDrawer();
        },
      },
    ],
    // Use undefined to get original button style (not the new variations)
    buttonStyle: undefined,
  });

  // Handle confirmation from drawer
  const handleConfirmUpdate = async () => {
    // Form was already validated before drawer opened
    const formValues = formRef.current.state.values;

    try {
      if (!user?.id) {
        throw new Error('User must be authenticated to create an event');
      }

      const selectedRoles = formValues.invitationGroups
        ? Array.from(formValues.invitationGroups)
        : [];
      const selectedGroupIds = formValues.eventGroupTargetIds
        ? Array.from(formValues.eventGroupTargetIds)
        : [];

      if (isEditMode && eventId) {
        // Update existing event
        const updates = formValuesToEventUpdate(formValues);
        const updatedEvent = await updateEvent.mutateAsync({
          id: eventId,
          updates,
        });
        await setEventRoleTargets(eventId, selectedRoles);
        await setEventGroupTargets(eventId, selectedGroupIds);
        let fileSyncError: unknown = null;

        try {
          await syncEventFiles.mutateAsync({
            eventId,
            files: materialFilesRef.current,
            uploadedByUserId: user.id,
          });
        } catch (error) {
          fileSyncError = error;
          console.error('Failed to sync event files:', error);
        }

        // Send notification if requested
        if (sendNotification) {
          try {
            await sendEventNotification(updatedEvent.id, {
              customMessage: notificationMessage || undefined,
              isNewEvent: false,
            });
          } catch (notificationError) {
            // Log but don't block navigation - notification failure shouldn't prevent event update
            console.error('Failed to send notification:', notificationError);
          }
        }

        setDrawerOpen(false);
        if (fileSyncError) {
          showToast({
            title: 'Event updated, materials not saved',
            description:
              fileSyncError instanceof Error
                ? fileSyncError.message
                : 'Save the event again to retry the material changes.',
            variant: 'error',
          });
        } else {
          showToast({
            title: 'Event updated',
            description: `"${updatedEvent.title}" has been updated successfully.`,
            variant: 'success',
          });
        }
        // Navigate back to event details
        // Use replace to remove the "Edit Event" form from history stack
        navigate({
          to: '/events/$eventId',
          params: { eventId: eventId.toString() },
          replace: true,
        });
      } else {
        // Create new event or event series
        const newEvent = formValuesToEventInsert(formValues, user.id);
        const isRecurring =
          formValues.recurrenceType !== 'none' &&
          formValues.recurrenceCount >= 2;

        let createdEvents: Awaited<
          ReturnType<typeof createEvent.mutateAsync>
        >[];

        if (isRecurring) {
          // Create event series
          createdEvents = await createEventSeries.mutateAsync({
            baseEvent: newEvent,
            recurrenceType: formValues.recurrenceType,
            recurrenceCount: formValues.recurrenceCount,
            createdBy: user.id,
          });
        } else {
          // Create single event
          const singleEvent = await createEvent.mutateAsync(newEvent);
          createdEvents = [singleEvent];
        }

        await Promise.all(
          createdEvents.map((createdEvent) =>
            Promise.all([
              setEventRoleTargets(createdEvent.id, selectedRoles),
              setEventGroupTargets(createdEvent.id, selectedGroupIds),
            ])
          )
        );

        let fileSyncError: unknown = null;

        try {
          await Promise.all(
            createdEvents.map((createdEvent, index) =>
              syncEventFiles.mutateAsync({
                eventId: createdEvent.id,
                files: isRecurring
                  ? getFilesForOccurrence(materialFilesRef.current, index)
                  : materialFilesRef.current,
                uploadedByUserId: user.id,
              })
            )
          );
        } catch (error) {
          fileSyncError = error;
          console.error('Failed to sync event files:', error);
        }

        // Send notification if requested (for the first event in the series)
        if (sendNotification && createdEvents.length > 0) {
          try {
            await sendEventNotification(createdEvents[0].id, {
              customMessage: notificationMessage || undefined,
              isNewEvent: true,
            });
          } catch (notificationError) {
            // Log but don't block navigation - notification failure shouldn't prevent event creation
            console.error('Failed to send notification:', notificationError);
          }
        }

        setDrawerOpen(false);
        // Show success toast
        const eventTitle = createdEvents[0]?.title || 'Event';
        if (fileSyncError) {
          showToast({
            title: 'Event created, materials not saved',
            description:
              fileSyncError instanceof Error
                ? fileSyncError.message
                : 'Edit the event to retry the material upload.',
            variant: 'error',
          });
        } else if (createdEvents.length > 1) {
          showToast({
            title: 'Event series created',
            description: `${createdEvents.length} events created successfully.`,
            variant: 'success',
          });
        } else {
          showToast({
            title: 'Event created',
            description: `"${eventTitle}" has been created successfully.`,
            variant: 'success',
          });
        }
        // Navigate to event details for the first created event
        // Use replace to remove the "New Event" form from history stack
        navigate({
          to: '/events/$eventId',
          params: { eventId: createdEvents[0].id.toString() },
          replace: true,
        });
      }
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? 'update' : 'create'} event:`,
        error
      );
      // Show error toast
      showToast({
        title: isEditMode ? 'Update failed' : 'Creation failed',
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`,
        variant: 'error',
      });
    }
  };

  // Handle delete confirmation from drawer
  const handleConfirmDelete = async () => {
    if (!isEditMode || !eventId) return;

    try {
      await deleteEvent.mutateAsync(eventId);
      setDeleteDrawerOpen(false);
      // Show success toast
      showToast({
        title: 'Event deleted',
        description: 'The event has been deleted successfully.',
        variant: 'success',
      });
      // Navigate back to events list after delete
      navigate({ to: '/events' });
    } catch (error) {
      console.error('Failed to delete event', error);
      // Show error toast
      showToast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete event. Please try again.',
        variant: 'error',
      });
    }
  };

  return {
    form,
    isEditMode,
    pageTitle: isEditMode ? 'Edit Event' : 'New Event',
    isLoadingEvent: isFormLoading,
    isLoadingEventFiles: isEditMode && isLoadingEventFiles,
    drawerOpen,
    setDrawerOpen,
    sendNotification,
    setSendNotification,
    notificationMessage,
    setNotificationMessage,
    handleConfirmUpdate,
    isUpdating: isEditMode
      ? updateEvent.isPending || syncEventFiles.isPending
      : createEvent.isPending ||
        createEventSeries.isPending ||
        syncEventFiles.isPending,
    deleteDrawerOpen,
    setDeleteDrawerOpen,
    handleConfirmDelete,
    isDeleting: deleteEvent.isPending,
    submitAttempted,
    validationErrors,
    materialFiles,
    materialErrors,
    recurrenceCount,
    isRecurringCreate,
    addMaterialFiles,
    updateMaterialTitle,
    removeMaterialFile,
    restoreMaterialFile,
    toggleMaterialOccurrence,
    toast,
    hideToast,
  };
}
